import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import {
  CreateEhsListItemInput,
  UpdateEhsListItemInput,
} from "../components/supportFunctions/graphql/EhsConfigurationsQueries";
import EhsConfigService from "../services/EhsConfigService";
import { EhsListItem } from "../components/routeComponents/EhsConfiguration/EhsListsManagement";

export enum EhsListItemActions {
  CREATE = "create",
  UPDATE = "update",
  ACTIVATE = "activate",
  INACTIVATE = "inactivate",
}

const useEhsListItemMutaion = (customerId: string) => {
  const create = useMutation(
    (req: CreateEhsListItemInput) => EhsConfigService.createEhsListItem(req),
    ResponseHandling("ehs_list_item_create_success")
  );
  const update = useMutation(
    (req: UpdateEhsListItemInput) => EhsConfigService.updateEhsListItem(req),
    ResponseHandling("ehs_list_item_update_success")
  );
  const activate = useMutation(
    (req: UpdateEhsListItemInput) => EhsConfigService.updateEhsListItem(req),
    ResponseHandling("ehs_list_item_activate_success")
  );
  const inactivate = useMutation(
    (req: UpdateEhsListItemInput) => EhsConfigService.updateEhsListItem(req),
    ResponseHandling("ehs_list_item_inactivate_success")
  );

  const mutate = async (action: EhsListItemActions, input: EhsListItem) => {
    switch (action) {
      case EhsListItemActions.CREATE:
        const createEhsListItem = mapFormToCreate(input, customerId);
        return await create.mutateAsync(createEhsListItem);
      case EhsListItemActions.UPDATE:
        const updateEhsListItem = mapFormToUpdate(input, customerId);
        return await update.mutateAsync(updateEhsListItem);
      case EhsListItemActions.ACTIVATE:
        const activateEhsListItem = mapFormToUpdate(input, customerId);
        return await activate.mutateAsync(activateEhsListItem);
      case EhsListItemActions.INACTIVATE:
        const inactivateEhsListItem = mapFormToUpdate(input, customerId);
        return await inactivate.mutateAsync(inactivateEhsListItem);
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: create.isLoading || update.isLoading,
      create: {
        isSuccess: create.isSuccess,
        data: create.data,
      },
      update: {
        isSuccess: update.isSuccess,
        data: update.data,
      },
    },
  };
};

export default useEhsListItemMutaion;

function mapFormToCreate(
  input: EhsListItem,
  customerId: string
): CreateEhsListItemInput {
  return {
    customerId,
    listTypeCode: input.listTypeCode,
    enabled: true,
    item: input.item,
    code: input.code,
    shortName: input.shortName,
    description: input.description,
    listOrder: input.listOrder,
  };
}

function mapFormToUpdate(
  input: EhsListItem,
  customerId: string
): UpdateEhsListItemInput {
  return {
    customerId,
    id: input.id,
    listTypeCode: input.listTypeCode,
    enabled: input.enabled,
    item: input.item,
    code: input.code,
    shortName: input.shortName,
    description: input.description,
    listOrder: input.listOrder,
  };
}

function ResponseHandling(messageKey: string = "ehs_list_item_update_success") {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
      queryClient.invalidateQueries({ queryKey: ["ehsLists"] });
    },
  };
}
